import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { PaymentOrder } from '../../../types';
import { findCodePrefix, nextSequence } from '../../../utils/codePrefixUtils';
import Input from '../Input';

interface OrderInfoSectionProps {
  register: UseFormRegister<PaymentOrder>;
  errors: FieldErrors<PaymentOrder>;
  codePrefixes: any[];
  selectedCodePrefix: string;
  setSelectedCodePrefix: (value: string) => void;
  orderSeqNumber: string;
  setOrderSeqNumber: (value: string) => void;
  setValue: (name: any, value: any, options?: any) => void;
  isEditing: boolean;
}

const OrderInfoSection: React.FC<OrderInfoSectionProps> = ({
  register,
  errors,
  codePrefixes,
  selectedCodePrefix,
  setSelectedCodePrefix,
  orderSeqNumber,
  setOrderSeqNumber,
  setValue,
  isEditing,
}) => {
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-lg font-semibold text-gray-900">Order Information</h2>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Order Number <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <select
                value={selectedCodePrefix}
                onChange={(e) => {
                  setSelectedCodePrefix(e.target.value);
                  const prefix = findCodePrefix(codePrefixes, e.target.value);
                  if (prefix) {
                    const nextNum = String(nextSequence(prefix)).padStart(3, '0');
                    setOrderSeqNumber(nextNum);
                    setValue('orderNumber', `${prefix.prefix}${nextNum}`, { shouldValidate: true });
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={isEditing}
              >
                <option value="">Select Code Prefix</option>
                {codePrefixes.map((prefix: any) => (
                  <option key={prefix.id} value={prefix.id}>
                    {prefix.prefix} - {prefix.name || prefix.description || ''}
                  </option>
                ))}
              </select>
            </div>
            {selectedCodePrefix && (
              <div className="mt-2 p-2 bg-gray-50 rounded-md border border-gray-200">
                <span className="text-sm text-gray-600">Generated Code: </span>
                <span className="text-sm font-semibold text-primary-600">
                  {findCodePrefix(codePrefixes, selectedCodePrefix)?.prefix}{orderSeqNumber}
                </span>
              </div>
            )}
            <input type="hidden" {...register('orderNumber', { required: 'Order number is required' })} />
            {errors.orderNumber?.message && (
              <p className="mt-1 text-sm text-red-600">{errors.orderNumber.message}</p>
            )}
          </div>
          <div>
            <Input
              label="Date"
              type="date"
              {...register('date', { required: 'Date is required' })}
              error={errors.date?.message}
            />
          </div>
          <div>
            <Input
              label="Reference No."
              {...register('referenceNo')}
              placeholder="Reference number (optional)"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderInfoSection;
